import React, { useRef, useEffect, useState, createContext } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react-lite';
import { Manager, Reference, Popper } from 'react-popper';

import ClickOutside from 'shared/layout/ClickOutside';

const { Provider, Consumer } = createContext();

export const MenuConsumer = Consumer;

function Menu({
  trigger,
  children,
  placement = 'bottom-start',
  menuTriggerHoverOff,
  menuTriggerStyle,
}) {
  const [open, setOpen] = useState(false);
  const [trans, setTrans] = useState(true);
  const timeoutRef = useRef();

  function onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    timeoutRef.current = setTimeout(() => {
      setTrans(!trans);
    }, 400);
    setOpen((old) => !old);
  }

  function close(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setOpen(false);
    timeoutRef.current = setTimeout(() => {
      setTrans(true);
    }, 400);
  }

  useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

  useEffect(() => {
    if (open) {
      const el = document.getElementById('root');
      el.style.pointerEvents = 'none';
      return () => (el.style.pointerEvents = 'auto');
    }
  }, [open]);

  return (
    <ClickOutside onOutsideClick={open ? close : null}>
      <Manager>
        <Reference>
          {({ ref }) => (
            <MenuTriggerContainer
              ref={ref}
              onClick={onTriggerClick}
              open={open}
              hoverOff={menuTriggerHoverOff}
              style={menuTriggerStyle}
            >
              {typeof trigger === 'function' ? trigger({ isOpen: open }) : trigger}
            </MenuTriggerContainer>
          )}
        </Reference>
        {createPortal(
          <Popper placement={placement} modifiers={popperModifiers}>
            {({ ref, style, placement, arrowProps }) => (
              <div ref={ref} style={{ ...style, zIndex: 100 }} data-placement={placement}>
                <Provider value={{ open, close }}>
                  <MenuWrapper open={open} trans={trans}>
                    {typeof children === 'function' ? children({ isOpen: open, close }) : children}
                    <div ref={arrowProps.ref} style={arrowProps.style} />
                  </MenuWrapper>
                </Provider>
              </div>
            )}
          </Popper>,
          document.body
        )}
      </Manager>
    </ClickOutside>
  );
}

export default observer(Menu);

let popperModifiers = {
  preventOverflow: { enabled: false },
  hide: { enabled: false },
};

const menuTriggerCss = css`
  background-color: ${(p) => p.theme.purpleTrans5};
  opacity: 1;
  cursor: pointer;
`;

const MenuWrapper = styled.div`
  font-family: ${(p) => p.theme.fontFamily};
  background-color: #fff;
  width: 205px;
  ${(p) => p.trans && 'transition: max-height ease-in 0.2s, opacity ease-in 0.1s'};
  opacity: ${(p) => (p.open ? 1 : 0)};
  max-height: ${(p) => (p.open ? '800px' : '0px')};
  overflow: hidden;
  z-index: ${(p) => (p.open ? 91 : -1)};
  border-radius: 4px;
  box-shadow: 0px 4px 40px rgba(0, 0, 0, 0.16);
  padding: ${(p) => (p.open ? '10px' : '0px')};
  left: 20px;
`;

const MenuTriggerContainer = styled.div`
  border-radius: 2px;
  position: relative;
  z-index: 89;
  min-height: 36px;
  min-width: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(p) => p.theme.purple100};
  opacity: 0.5;
  transition: all 200ms ease;
  ${(p) =>
    !p.hoverOff &&
    css`
      :hover {
        ${menuTriggerCss}
      }
    `};
  ${(p) => !p.hoverOff && p.open && menuTriggerCss}
`;
